import { getCustomer, getOrCreateCustomer } from '../notice/customer';
import { getModelFromSnapshot } from '../model';
import { safeGetModelArrayFromQuery } from '../model/getModel';
import { CustomerModel } from '../model/objects/customerModel';
import { OrganizationModel } from '../model/objects/organizationModel';
import { UserModel } from '../model/objects/userModel';
import {
  ResponseOrColumnError,
  ResponseOrError,
  wrapError,
  wrapSuccess
} from '../types/responses';
import {
  Customer,
  EFirebaseContext,
  EOrganization,
  ERef,
  ESnapshotExists,
  EUser,
  exists
} from '../types';
import { safeAsync } from '../safeWrappers';
import { getErrorReporter } from '../utils/errors';
import { ColumnService } from './directory';

class CustomerService {
  public constructor(private context: EFirebaseContext) {}

  public async maybeGetCustomerFromUserAndOrg(
    user: ERef<EUser>,
    organization: ERef<EOrganization>
  ): Promise<ResponseOrError<ESnapshotExists<Customer> | null>> {
    const [userError, userSnap] = await safeAsync(() => user.get())();
    if (userError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.DATABASE,
        userError,
        'Error getting user in customer service',
        {
          userId: user.id
        }
      );
      return wrapError(userError);
    }
    const [organizationError, organizationSnap] = await safeAsync(() =>
      organization.get()
    )();
    if (organizationError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.DATABASE,
        userError,
        'Error getting newspaper in customer service',
        {
          organizationId: organization.id
        }
      );
      return wrapError(organizationError);
    }
    return await safeAsync(() =>
      getCustomer(this.context, userSnap, organizationSnap)
    )();
  }

  /**
   * The customer record for this filer at this publisher as a model, creating
   * it when absent. Integration code needs the model (billing term, internal
   * id) and must not touch refs, so both the reads and the create live here.
   */
  public async getOrCreateCustomerModel(
    user: UserModel,
    publisher: OrganizationModel
  ): Promise<ResponseOrError<CustomerModel>> {
    const [userSnapError, userSnap] = await safeAsync(() => user.ref.get())();
    if (userSnapError) {
      return wrapError(userSnapError);
    }
    const [publisherSnapError, publisherSnap] = await safeAsync(() =>
      publisher.ref.get()
    )();
    if (publisherSnapError) {
      return wrapError(publisherSnapError);
    }
    const [customerError, customer] = await safeAsync(() =>
      getOrCreateCustomer(this.context, userSnap, publisherSnap)
    )();
    if (customerError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.DATABASE,
        customerError,
        'Failed to get or create customer',
        { userId: user.id, publisherId: publisher.id }
      );
      return wrapError(customerError);
    }
    return wrapSuccess(
      getModelFromSnapshot(CustomerModel, this.context, customer)
    );
  }

  /**
   * Every customer record this user has, across publishers. Used to find an ad
   * system id the same filer already has at another paper in a group.
   */
  public async findCustomersForUser(
    user: UserModel
  ): Promise<ResponseOrColumnError<CustomerModel[]>> {
    return safeGetModelArrayFromQuery(
      CustomerModel,
      this.context,
      this.context.customersRef().where('user', '==', user.ref)
    );
  }

  async getCustomerFromUserAndOrg(
    user: ERef<EUser>,
    organization: ERef<EOrganization>
  ) {
    const [customerError, customer] = await this.maybeGetCustomerFromUserAndOrg(
      user,
      organization
    );
    if (customerError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.DATABASE,
        customerError,
        'Error getting customer in service',
        {
          user: user.id,
          organization: organization.id
        }
      );
      return wrapError(customerError);
    }

    if (!exists(customer)) {
      getErrorReporter().logAndCaptureWarning(
        'Expected customer does not exist',
        {
          user: user.id,
          organization: organization.id
        }
      );
      return wrapError(
        new Error(
          `Customer with anticipated ID does not exist: ${user.id}_${organization.id}`
        )
      );
    }
    return wrapSuccess(customer);
  }
}

export default CustomerService;
